import { format } from 'date-fns';
import { BundleFilterOptions, EnabledStates, GetBundleSectionsProps, SelectedValues } from '@/api';
import {
  PROVIDERS_FILTER_GROUP_NAME,
  FILTER_BLANKS_KEY,
  FILTER_NON_BLANKS_KEY,
  FILTER_EXTRACTED_KEY,
  FILTER_DUPLICATES_KEY,
} from '@/constants/filters';
import { isProvidersFilter } from './isProvidersFilter';

interface BundleSectionsBodyParams {
  bundleId: string;
  selectedValues: SelectedValues;
  enabledStates: EnabledStates;
  bundleFilterOptions: BundleFilterOptions[];
}

type BundleSectionsBody = Omit<GetBundleSectionsProps, 'abortController'>;
type BundleSectionsInitialAccumulator = {
  bundleId: string;
  filterBlanks?: boolean;
  filterNonBlanks?: boolean;
  filterExtracted?: boolean;
  filterDuplicates?: boolean;
};

/** Generate body for /bundle/sections endpoint */
export const getBundleSectionsBody = ({
  bundleId,
  selectedValues,
  enabledStates,
  bundleFilterOptions,
}: BundleSectionsBodyParams) => {
  return Object.entries(selectedValues).reduce(
    (acc: BundleSectionsBody, [key, value]) => {
      /** displayType === 'dates' */
      if (key === 'dates' && value?.length && enabledStates[key]) {
        const [startDate, endDate] = value;
        return {
          ...acc,
          ...(startDate && { startDate: format(startDate, 'yyyy-MM-dd') }),
          ...(endDate && { endDate: format(endDate, 'yyyy-MM-dd') }),
        };
      }

      /** displayType === 'drop_down' for Providers */
      if (key === PROVIDERS_FILTER_GROUP_NAME && value?.length && enabledStates[key]) {
        return {
          ...acc,
          providerIds: [...(acc.providerIds || []), ...value],
        };
      }

      /** displayType === 'drop_down' */
      if (key !== 'dates' && value?.length && enabledStates[key]) {
        return {
          ...acc,
          textLabelIds: [...(acc.textLabelIds ? acc.textLabelIds : []), ...value],
        };
      }

      /** displayType === 'checkbox' */
      if (
        bundleFilterOptions.find((item) => !isProvidersFilter(item) && item.groupName === key)?.displayType ===
          'checkbox' &&
        value?.length
      ) {
        return {
          ...acc,
          pageLabelIds: [...(acc.pageLabelIds ? acc.pageLabelIds : []), ...value],
        };
      }

      return acc;
    },
    {
      bundleId,
      filterBlanks: enabledStates[FILTER_NON_BLANKS_KEY] ? false : !enabledStates[FILTER_BLANKS_KEY],
      filterNonBlanks: !!enabledStates[FILTER_NON_BLANKS_KEY],
      filterExtracted: !!enabledStates[FILTER_EXTRACTED_KEY],
      filterDuplicates: !!enabledStates[FILTER_DUPLICATES_KEY],
    } as BundleSectionsInitialAccumulator,
  );
};
